<template>
  <div>
    <!-- 固定顶部导航栏 -->
    <StickyNav :is-visible="isScrolled" />

    <!-- Header 包含封面和内容 -->
    <AppHeader :title="article.title" :subtitle="article.subtitle">
      <!-- 面包屑导航 -->
      <div class="container mx-auto px-4 pt-6 pb-4 max-w-[1800px]">
        <Breadcrumb
          :topic-id="article.topicId"
          :topic-name="article.topic"
          :topic-icon="article.topicIcon"
          :current-page="article.title"
        />
      </div>

      <!-- 主要内容区域 -->
      <section class="container mx-auto px-4 pb-8 sm:pb-12 max-w-[1800px]">
        <div class="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
          <!-- 文章内容 -->
          <article
            class="w-full lg:w-[72%] xl:w-[74%] bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
          >
            <!-- 文章装饰头部 -->
            <div
              class="relative h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
            ></div>

            <div class="p-8 sm:p-10 lg:p-12">
              <div
                class="markdown-body prose prose-lg max-w-none"
                v-html="renderedMarkdown"
              ></div>
            </div>

            <!-- 文章底部装饰 -->
            <div class="px-8 sm:px-10 lg:px-12 pb-8">
              <div
                class="pt-6 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500"
              >
                <div class="flex items-center gap-4">
                  <span
                    class="flex items-center gap-1.5 text-purple-600 font-medium"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path
                        fill-rule="evenodd"
                        d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <span>{{ viewCount }} 次阅读</span>
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    @click="handleShare"
                    class="px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 text-blue-600 hover:from-blue-100 hover:to-purple-100 transition-all duration-200 flex items-center gap-1.5 text-xs font-medium"
                  >
                    <svg
                      class="w-3.5 h-3.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"
                      />
                    </svg>
                    分享
                  </button>
                </div>
              </div>

              <!-- 文章导航 - 上一篇/下一篇 -->
              <ArticleNavigation
                :prev-article="prevArticle"
                :next-article="nextArticle"
              />
            </div>
          </article>

          <!-- 右侧目录 -->
          <aside class="w-full lg:w-[28%] xl:w-[26%] lg:sticky lg:top-24">
            <div
              class="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 backdrop-blur-sm"
            >
              <!-- 目录标题 -->
              <div class="relative px-5 py-4 overflow-hidden">
                <div
                  class="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 opacity-90"
                ></div>
                <div
                  class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"
                ></div>
                <div class="relative flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-lg"
                  >
                    <svg
                      class="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2.5"
                        d="M4 6h16M4 12h16M4 18h7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-base font-bold text-white tracking-wide">
                      目录导航
                    </h3>
                    <p class="text-xs text-white/80 mt-0.5">
                      Table of Contents
                    </p>
                  </div>
                </div>
              </div>

              <!-- 目录内容 -->
              <nav
                class="toc-nav p-4 max-h-[calc(100vh-220px)] overflow-y-auto"
              >
                <ul class="space-y-0.5">
                  <li
                    v-for="(heading, index) in tableOfContents"
                    :key="heading.id"
                    :class="[
                      'toc-item',
                      `toc-level-${heading.level}`,
                      { active: activeHeading === heading.id },
                    ]"
                  >
                    <a
                      :href="`#${heading.id}`"
                      @click.prevent="scrollToHeading(heading.id)"
                      class="group block text-sm py-2.5 px-3 rounded-lg transition-all duration-300 relative overflow-hidden"
                      :style="{
                        paddingLeft: `${(heading.level - 1) * 14 + 12}px`,
                      }"
                    >
                      <span class="relative z-10 flex items-center gap-2">
                        <span
                          class="toc-number text-xs font-semibold opacity-50"
                          >{{ index + 1 }}</span
                        >
                        <span class="toc-text flex-1">{{ heading.text }}</span>
                      </span>
                    </a>
                  </li>
                </ul>
              </nav>

              <!-- 底部装饰 -->
              <div
                class="px-5 py-3 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-100"
              >
                <div
                  class="flex items-center justify-between text-xs text-gray-500"
                >
                  <span class="flex items-center gap-1">
                    <svg
                      class="w-3.5 h-3.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                      <path
                        fill-rule="evenodd"
                        d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    {{ tableOfContents.length }} 章节
                  </span>
                  <span class="text-blue-500 font-medium">快速导航</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </AppHeader>

    <!-- 评论区 -->
    <section class="relative z-10 bg-section py-8 sm:py-12">
      <div class="container mx-auto px-4 max-w-[1800px]">
        <div class="w-full lg:w-[72%] xl:w-[74%]">
          <CommentSection
            :article-id="route.params.id || 'word-of-the-day-2'"
            repo="your-username/your-repo"
            repo-id="YOUR_REPO_ID"
            category="Announcements"
            category-id="YOUR_CATEGORY_ID"
          />
        </div>
      </div>
    </section>

    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import { marked } from "marked";
import StickyNav from "@/components/StickyNav.vue";
import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import ArticleNavigation from "@/components/ArticleNavigation.vue";
import CommentSection from "@/components/CommentSection.vue";
import { useScrollNav } from "@/composables/useScrollNav";
import { loadArticle } from "@/utils/markdownLoader";
import { useBlogPosts } from "@/composables/useBlogPosts";
import { getTopicById } from "@/config/topics";
import { useLoading } from "@/composables/useLoading";
import { useCopyToast } from "@/composables/useCopyToast";
import {
  getViewCount,
  incrementViewCount,
  hasViewedInSession,
  markAsViewedInSession,
} from "@/utils/viewCounter";

export default {
  name: "BlogDetail",
  components: {
    StickyNav,
    AppHeader,
    AppFooter,
    Breadcrumb,
    ArticleNavigation,
    CommentSection,
  },
  setup() {
    const route = useRoute();
    const { isScrolled } = useScrollNav();
    const { showLoading, hideLoading } = useLoading();
    const { copyToClipboard } = useCopyToast();
    const { posts } = useBlogPosts();

    const article = ref({
      title: "Loading...",
      subtitle: "",
      date: "",
      author: "",
      topicId: "",
      topic: "",
      topicIcon: "",
    });

    const markdownContent = ref("");
    const renderedMarkdown = ref("");
    const tableOfContents = ref([]);
    const activeHeading = ref("");
    const loading = ref(true);
    const prevArticle = ref(null);
    const nextArticle = ref(null);
    const viewCount = ref(0);

    // 更新阅读次数
    const updateViewCount = (articleId) => {
      // 检查是否在当前会话中已经查看过
      if (!hasViewedInSession(articleId)) {
        // 增加阅读次数
        const count = incrementViewCount(articleId);
        viewCount.value = count;
        // 标记为已查看
        markAsViewedInSession(articleId);
      } else {
        // 只获取阅读次数，不增加
        viewCount.value = getViewCount(articleId);
      }
    };

    // 分享按钮点击事件
    const handleShare = () => {
      const currentUrl = window.location.href;
      copyToClipboard(currentUrl);
    };

    // 自定义 marked 渲染器
    const renderer = new marked.Renderer();
    let headings = [];

    // 修复标题渲染 - 支持中文和特殊字符，只收集 h1-h4
    renderer.heading = function (text, level) {
      // 移除 HTML 标签，生成更好的 ID
      const cleanText = text.replace(/<[^>]*>/g, "");
      // 使用更好的 ID 生成策略 - 保留点号和数字编号
      const id = cleanText
        .trim()
        .replace(/\s+/g, "-") // 空格转为连字符
        .replace(/[^\w\u4e00-\u9fa5.-]/g, "") // 保留字母、数字、中文、连字符、点号
        .replace(/--+/g, "-") // 多个连字符合并为一个
        .replace(/^-+|-+$/g, "") // 移除首尾连字符
        .toLowerCase(); // 转小写

      // 只收集 h1-h4 级别的标题到目录
      if (level >= 1 && level <= 4) {
        headings.push({ id, text: cleanText, level });
      }

      return `<h${level} id="${id}" class="heading-anchor">${text}</h${level}>`;
    };

    // 修复图片渲染 - 支持相对路径和多种格式
    renderer.image = function (href, title, text) {
      let imageSrc = href;

      // 如果是相对路径（不是 http/https/data 开头）
      if (
        !href.startsWith("http") &&
        !href.startsWith("data:") &&
        !href.startsWith("/")
      ) {
        try {
          // 尝试从 pictures 目录加载
          imageSrc = require(`@/assets/pictures/${href}`);
        } catch (e) {
          try {
            // 尝试从 articles 同级目录加载
            imageSrc = require(`@/assets/articles/${href}`);
          } catch (e2) {
            console.warn(`Image not found: ${href}`);
            // 保持原路径，让浏览器尝试加载
            imageSrc = href;
          }
        }
      }

      const titleAttr = title ? ` title="${title}"` : "";
      const altAttr = text ? ` alt="${text}"` : ' alt=""';
      return `<img src="${imageSrc}"${altAttr}${titleAttr} loading="lazy" />`;
    };

    marked.setOptions({
      renderer: renderer,
      breaks: true,
      gfm: true,
    });

    const parseMarkdown = () => {
      headings = []; // 清空标题数组
      renderedMarkdown.value = marked(markdownContent.value);
      tableOfContents.value = [...headings]; // 复制所有标题
    };

    // 查找上一篇和下一篇文章
    const findAdjacentArticles = (currentArticleId) => {
      const currentIndex = posts.value.findIndex(
        (post) => post.id === currentArticleId
      );

      if (currentIndex !== -1) {
        // 上一篇（索引-1）
        if (currentIndex > 0) {
          prevArticle.value = posts.value[currentIndex - 1];
        } else {
          prevArticle.value = null;
        }

        // 下一篇（索引+1）
        if (currentIndex < posts.value.length - 1) {
          nextArticle.value = posts.value[currentIndex + 1];
        } else {
          nextArticle.value = null;
        }
      }
    };

    // 加载文章内容
    const loadArticleContent = async () => {
      loading.value = true;
      showLoading("加载文章中...");

      try {
        const articleId = route.params.id;
        if (!articleId) {
          throw new Error("Article ID is missing");
        }

        // 解码 URL 参数（处理中文和特殊字符）
        const decodedArticleId = decodeURIComponent(articleId);
        const articleData = await loadArticle(decodedArticleId);

        // 获取分类信息
        const topic = getTopicById(articleData.topic || "other");

        article.value = {
          title: articleData.title,
          subtitle: `${articleData.date} • ${articleData.author}`,
          date: articleData.date,
          author: articleData.author,
          topicId: topic.id,
          topic: topic.name,
          topicIcon: topic.icon,
        };

        markdownContent.value = articleData.content;
        parseMarkdown();

        // 查找上一篇和下一篇文章
        findAdjacentArticles(decodedArticleId);

        // 更新阅读次数
        updateViewCount(decodedArticleId);
      } catch (error) {
        console.error("Error loading article:", error);
        article.value.title = "Article Not Found";
        markdownContent.value =
          "# Article Not Found\n\nThe requested article could not be loaded.";
        parseMarkdown();
      } finally {
        loading.value = false;
        setTimeout(() => {
          hideLoading();
        }, 300);
      }
    };

    const scrollToHeading = (id) => {
      const element = document.getElementById(id);
      if (element) {
        const offset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        activeHeading.value = id;
      }
    };

    // 节流函数
    const throttle = (func, delay) => {
      let timeoutId = null;
      let lastRan = 0;
      return function (...args) {
        const now = Date.now();
        if (now - lastRan >= delay) {
          func.apply(this, args);
          lastRan = now;
        } else {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            func.apply(this, args);
            lastRan = Date.now();
          }, delay - (now - lastRan));
        }
      };
    };

    const handleScroll = () => {
      const headingElements = document.querySelectorAll(".heading-anchor");
      if (headingElements.length === 0) return;

      let currentHeading = "";
      const scrollPosition = window.scrollY + 200; // 增加偏移量

      // 找到当前滚动位置最接近的标题
      let minDistance = Infinity;
      headingElements.forEach((heading) => {
        const rect = heading.getBoundingClientRect();
        const elementTop = rect.top + window.scrollY;
        const distance = Math.abs(scrollPosition - elementTop);

        // 如果标题在视口上方且距离最近
        if (elementTop <= scrollPosition && distance < minDistance) {
          minDistance = distance;
          currentHeading = heading.id;
        }
      });

      // 如果没有找到（页面顶部），使用第一个标题
      if (!currentHeading && headingElements.length > 0) {
        currentHeading = headingElements[0].id;
      }

      activeHeading.value = currentHeading;
    };

    onMounted(() => {
      loadArticleContent();
      const throttledScroll = throttle(handleScroll, 100);
      window.addEventListener("scroll", throttledScroll);
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    // 监听路由变化
    watch(
      () => route.params.id,
      () => {
        loadArticleContent();
      }
    );

    return {
      route,
      isScrolled,
      article,
      renderedMarkdown,
      tableOfContents,
      activeHeading,
      scrollToHeading,
      loading,
      handleShare,
      prevArticle,
      nextArticle,
      viewCount,
    };
  },
};
</script>

<style scoped>
/* 目录样式 */
.toc-nav {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}

.toc-nav::-webkit-scrollbar {
  width: 5px;
}

.toc-nav::-webkit-scrollbar-track {
  background: #f8fafc;
}

.toc-nav::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, #cbd5e1, #94a3b8);
  border-radius: 10px;
}

.toc-nav::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to bottom, #94a3b8, #64748b);
}

.toc-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.toc-item a {
  color: #64748b;
  font-weight: 400;
  position: relative;
}

.toc-item a::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%);
  border-radius: 0 4px 4px 0;
  transition: width 0.3s ease;
  opacity: 0;
}

.toc-item a:hover::before {
  width: 3px;
  opacity: 1;
}

.toc-item a:hover {
  background: linear-gradient(to right, #eff6ff, transparent);
  color: #2563eb;
}

.toc-item a:hover .toc-number {
  color: #3b82f6;
  opacity: 1;
}

.toc-item.active a {
  color: #2563eb;
  font-weight: 600;
  background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
  box-shadow: inset 0 1px 3px rgba(59, 130, 246, 0.1);
}

.toc-item.active a::before {
  width: 3px;
  opacity: 1;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
}

.toc-item.active .toc-number {
  color: #3b82f6;
  opacity: 1;
  font-weight: 700;
}

.toc-item.active .toc-text {
  color: #1e40af;
}

.toc-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  border-radius: 6px;
  transition: all 0.3s ease;
}

.toc-item.active .toc-number {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
}

/* Markdown 内容样式 */
:deep(.markdown-body) {
  color: #1f2937;
  line-height: 1.8;
  font-size: 16px;
}

:deep(.markdown-body h1) {
  font-size: 2.5rem;
  font-weight: 800;
  margin-top: 0;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:deep(.markdown-body h1::after) {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(
    to right,
    transparent,
    #3b82f6,
    #8b5cf6,
    #ec4899,
    transparent
  );
  border-radius: 2px;
}

:deep(.markdown-body h2) {
  font-size: 2rem;
  font-weight: 700;
  margin-top: 3rem;
  margin-bottom: 1.25rem;
  color: #1f2937;
  padding-left: 1rem;
  position: relative;
}

:deep(.markdown-body h2::before) {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 70%;
  background: linear-gradient(to bottom, #3b82f6, #8b5cf6);
  border-radius: 2px;
}

:deep(.markdown-body h2::after) {
  content: "";
  position: absolute;
  left: 0;
  bottom: -8px;
  width: 60px;
  height: 2px;
  background: linear-gradient(to right, #3b82f6, #8b5cf6);
  border-radius: 1px;
}

:deep(.markdown-body h3) {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: #374151;
  padding-left: 0.75rem;
  border-left: 3px solid transparent;
  border-image: linear-gradient(to bottom, #60a5fa, #a78bfa) 1;
}

:deep(.markdown-body h4) {
  font-size: 1.25rem;
  font-weight: 700;
  margin-top: 1.5rem;
  margin-bottom: 0.875rem;
  color: #1f2937;
}

:deep(.markdown-body p) {
  margin-bottom: 1.25rem;
  color: #4b5563;
  text-align: justify;
}

:deep(.markdown-body strong) {
  font-weight: 700;
  color: #1f2937;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  padding: 2px 6px;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(251, 191, 36, 0.1);
}

:deep(.markdown-body em) {
  font-style: italic;
  color: #6b7280;
  position: relative;
}

:deep(.markdown-body code) {
  background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.9em;
  color: #dc2626;
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  border: 1px solid #bfdbfe;
  box-shadow: 0 1px 2px rgba(59, 130, 246, 0.1);
}

:deep(.markdown-body pre) {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #f1f5f9;
  padding: 1.5rem;
  border-radius: 12px;
  overflow-x: auto;
  margin: 1.5rem 0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #334155;
  position: relative;
}

:deep(.markdown-body pre::before) {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899);
  border-radius: 12px 12px 0 0;
}

:deep(.markdown-body pre code) {
  background: transparent;
  color: inherit;
  padding: 0;
  border: none;
  font-size: 0.95em;
  line-height: 1.6;
  box-shadow: none;
}

:deep(.markdown-body ul),
:deep(.markdown-body ol) {
  margin-left: 1.75rem;
  margin-bottom: 1.25rem;
  color: #4b5563;
}

:deep(.markdown-body li) {
  margin-bottom: 0.75rem;
  line-height: 1.7;
  position: relative;
}

:deep(.markdown-body ul > li::marker) {
  color: transparent;
}

:deep(.markdown-body ul > li::before) {
  content: "";
  position: absolute;
  left: -1.5rem;
  top: 0.6em;
  width: 6px;
  height: 6px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

:deep(.markdown-body ol > li::marker) {
  color: #3b82f6;
  font-weight: 700;
}

:deep(.markdown-body blockquote) {
  border-left: 4px solid transparent;
  border-image: linear-gradient(to bottom, #3b82f6, #8b5cf6) 1;
  background: linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%);
  padding: 1rem 1.5rem;
  margin: 1.5rem 0;
  color: #475569;
  font-style: italic;
  border-radius: 0 12px 12px 0;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  position: relative;
}

:deep(.markdown-body blockquote::before) {
  content: '"';
  position: absolute;
  top: -10px;
  left: 10px;
  font-size: 3rem;
  color: #3b82f6;
  opacity: 0.2;
  font-family: Georgia, serif;
}

:deep(.markdown-body a) {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
  background: linear-gradient(to right, #3b82f6, #8b5cf6);
  background-size: 0% 2px;
  background-repeat: no-repeat;
  background-position: left bottom;
  transition: all 0.3s ease;
  padding-bottom: 2px;
}

:deep(.markdown-body a:hover) {
  color: #1d4ed8;
  background-size: 100% 2px;
}

:deep(.markdown-body img) {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
  margin: 2rem 0;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 3px solid transparent;
  background: linear-gradient(white, white) padding-box,
    linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899) border-box;
}

:deep(.markdown-body hr) {
  border: none;
  height: 3px;
  background: linear-gradient(
    to right,
    transparent,
    #3b82f6,
    #8b5cf6,
    #ec4899,
    transparent
  );
  margin: 3rem 0;
  border-radius: 2px;
}

:deep(.markdown-body table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

:deep(.markdown-body thead) {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
}

:deep(.markdown-body th) {
  border: none;
  padding: 1rem;
  text-align: left;
  color: white;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.05em;
}

:deep(.markdown-body td) {
  border: 1px solid #e5e7eb;
  padding: 0.875rem 1rem;
  text-align: left;
}

:deep(.markdown-body tbody tr) {
  transition: all 0.2s ease;
}

:deep(.markdown-body tbody tr:nth-child(even)) {
  background-color: #f9fafb;
}

:deep(.markdown-body tbody tr:hover) {
  background: linear-gradient(to right, #eff6ff, #f5f3ff);
  transform: scale(1.01);
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
}

/* 标题锚点样式 */
:deep(.heading-anchor) {
  scroll-margin-top: 120px;
}
</style>
